import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { THEME_COLOR_NAMES, ThemeColorName } from '@examdojo/styles';

export interface ColorConfig {
  solidClass: string;
  outlineClass: string;
}

export const COLOR_CONFIG: Record<ThemeColorName, ColorConfig> = {
  amber: { solidClass: 'bg-amber-500', outlineClass: 'outline-amber-500' },
  green: { solidClass: 'bg-green-500', outlineClass: 'outline-green-500' },
  indigo: { solidClass: 'bg-indigo-500', outlineClass: 'outline-indigo-500' },
  neutral: { solidClass: 'bg-neutral-500', outlineClass: 'outline-neutral-500' },
  orange: { solidClass: 'bg-orange-500', outlineClass: 'outline-orange-500' },
  pink: { solidClass: 'bg-pink-500', outlineClass: 'outline-pink-500' },
  purple: { solidClass: 'bg-purple-500', outlineClass: 'outline-purple-500' },
  red: { solidClass: 'bg-red-500', outlineClass: 'outline-red-500' },
  sky: { solidClass: 'bg-sky-500', outlineClass: 'outline-sky-500' },
  teal: { solidClass: 'bg-teal-500', outlineClass: 'outline-teal-500' },
};

@Component({
  selector: 'dojo-color-picker',
  template: `
    <div
      class="grid place-items-center gap-4"
      [style.grid-template-columns]="'repeat(' + gridCols() + ', minmax(0, 1fr))'"
    >
      @for (color of colors(); track color) {
        <button
          type="button"
          class="size-6 rounded-full"
          [class]="colorClasses()[color]"
          (click)="selectedColor.set(color)"
        ></button>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorPickerComponent {
  readonly selectedColor = model<ThemeColorName>('indigo');
  readonly colors = input<readonly ThemeColorName[]>(THEME_COLOR_NAMES);
  readonly gridCols = input(5);

  readonly colorClasses = computed(() => {
    const selected = this.selectedColor();
    return Object.fromEntries(
      this.colors().map((color) => {
        const colorConfig = COLOR_CONFIG[color];
        const isSelected = color === selected;
        const classes = isSelected
          ? [colorConfig.solidClass, 'outline outline-1 outline-offset-2', colorConfig.outlineClass]
          : [colorConfig.solidClass];
        return [color, classes.join(' ')];
      }),
    ) as Record<ThemeColorName, string>;
  });
}
